import { useEffect } from "react";
import {
  CELEBRATION_EMOJI,
  DISAPPOINTMENT_EMOJI,
  OptionSide,
  REVEAL_WINDOW_MS,
} from "@badorbad/shared";
import type { IGuessResult } from "@badorbad/shared";
import { AvatarFace } from "../design/AvatarFace";
import { FitText } from "../design/FitText";
import { TimerBar } from "../design/TimerBar";
import { nameColorFor } from "../design/seed";
import { Cue, play } from "../design/sound";
import { NAME_COLORS, OptionSideKey, SIDES, palette } from "../design/tokens";
import { RevealStage, useRevealSequence } from "../design/useRevealSequence";
import { t, fmt } from "../locale";
import { useGame, selectIsHost, type Player } from "../stores/game";
import { Reactions } from "./Reactions";

/**
 * The reveal.
 *
 * Both options stay up through a 3-2-1, because the last three seconds before
 * an answer are the only ones where the room is still wrong together. Then the
 * option nobody picked goes dark, the picked one travels to the top and takes
 * the frame, and the author signs it. Underneath, the only two things anyone
 * actually wants: whether *you* read them right, and who else did.
 *
 * The halves are absolutely positioned rather than stacked in flow, so the
 * bottom one can physically rise to the top instead of the layout reflowing
 * around a removed element.
 */

const FROM_SIDE: Record<OptionSide, OptionSideKey> = {
  [OptionSide.A]: OptionSideKey.A,
  [OptionSide.B]: OptionSideKey.B,
};

/** One player in a cluster, at a size that survives a phone held up to a table. */
const ClusterFace = ({
  player,
  tone,
}: {
  player: Player | undefined;
  tone: string;
}) => (
  <div className="flex w-14 flex-col items-center gap-1">
    <AvatarFace seed={player?.avatar_seed ?? ""} size={38} />
    <span
      className="w-full truncate text-center text-[10px] leading-tight"
      style={{ color: tone }}
    >
      {player?.display_name ?? ""}
    </span>
  </div>
);

const Cluster = ({
  label,
  tone,
  guesses,
  players,
}: {
  label: string;
  tone: string;
  guesses: IGuessResult[];
  players: Player[];
}) => (
  <div className="flex flex-1 flex-col items-center gap-2">
    <div className="flex items-baseline gap-1.5">
      <span
        className="text-base font-bold tabular-nums"
        style={{ color: tone }}
      >
        {guesses.length}
      </span>
      <span
        className="text-[10px] font-bold uppercase tracking-widest"
        style={{ color: tone, opacity: 0.75 }}
      >
        {label}
      </span>
    </div>
    <div className="flex flex-wrap justify-center gap-1">
      {guesses.map((g) => (
        <ClusterFace
          key={g.user_id}
          player={players.find((p) => p.user_id === g.user_id)}
          tone={palette.inkDim}
        />
      ))}
    </div>
  </div>
);

export const Reveal = () => {
  const reveal = useGame((s) => s.reveal);
  const players = useGame((s) => s.players);
  const myUserId = useGame((s) => s.myUserId);
  const isHost = useGame(selectIsHost);
  const advanceReveal = useGame((s) => s.advanceReveal);
  const react = useGame((s) => s.react);

  const { stage, count } = useRevealSequence(reveal);
  const showSummary = stage >= RevealStage.Summary;
  const mine = reveal?.guesses.find((g) => g.user_id === myUserId);
  const amTarget = reveal?.target_id === myUserId;

  // The reveal's 3-2-1 is its own countdown, independent of the longer
  // server-controlled reveal window shown in the timer bar.
  useEffect(() => {
    if (reveal && stage === RevealStage.Countdown && count > 0) play(Cue.TimeLow);
  }, [reveal, stage, count]);

  // Outcome audio is personal: guessers hear whether *their* read was right.
  // The target made a choice rather than a guess, so no success/failure cue is
  // meaningful for them. Invalid rounds count as failure for every guesser.
  useEffect(() => {
    if (!reveal || !showSummary || amTarget) return;
    play(reveal.valid && mine?.correct ? Cue.Success : Cue.Failure);
  }, [reveal, showSummary, amTarget, mine?.correct]);

  if (!reveal) return null;

  const named = (id: string) => players.find((p) => p.user_id === id);
  const target = named(reveal.target_id);
  const author = named(reveal.author_id);

  const chosenKey = reveal.target_choice
    ? FROM_SIDE[reveal.target_choice]
    : null;

  const counting = stage === RevealStage.Countdown;
  const risen = stage >= RevealStage.Rise;
  const showAuthor = stage >= RevealStage.Author;
  const choseParts = t.reveal.chose.split("%s");

  /*
   * A guesser who never answered counts as incorrect (plan.md §1.7), so they
   * belong in the wrong cluster rather than a third group of their own.
   */
  const right = reveal.guesses.filter((g) => g.correct);
  const wrong = reveal.guesses.filter((g) => !g.correct);

  const celebrating = !reveal.valid
    ? false
    : amTarget
      ? reveal.correct_count === 0
      : (mine?.correct ?? false);

  const emojiSet = celebrating ? CELEBRATION_EMOJI : DISAPPOINTMENT_EMOJI;

  /*
   * Which card is left standing at the end.
   *
   * On an invalid turn there is no winner, so this falls back to the top card —
   * the one already at `top: 0` that does not have to move. It is emptied of
   * its text below, because raising either option intact would read as "this is
   * what they picked" when the whole point is that nobody picked anything.
   *
   * Previously neither half counted as chosen here, so both ended up at
   * `top: 0, height: 100%, opacity: 1` — stacked, with B covering A purely by
   * DOM order, and option B presented as the answer.
   */
  const survivingKey = chosenKey ?? OptionSideKey.A;

  /** Where a half sits once the sequence has resolved. */
  const halfStyle = (key: OptionSideKey) => {
    const survives = survivingKey === key;
    const startTop = key === OptionSideKey.A ? "0%" : "50%";

    if (!risen) {
      return {
        top: startTop,
        height: "50%",
        // The loser goes dark the moment the countdown ends, before anything
        // moves — the darkening *is* the answer, so it must not share a beat.
        opacity: stage >= RevealStage.Fade && !survives ? 0 : 1,
      };
    }

    return { top: "0%", height: "100%", opacity: survives ? 1 : 0 };
  };

  return (
    <div className="relative mx-auto flex h-full max-w-md flex-col overflow-hidden">
      <TimerBar
        deadlineAt={new Date(reveal.deadline_at).getTime()}
        durationMs={REVEAL_WINDOW_MS}
      />

      {/* The stage. Full height until the summary needs room. */}
      <div
        className="relative min-h-0 overflow-hidden transition-all duration-500"
        style={{
          flexGrow: showSummary ? 0 : 1,
          flexBasis: showSummary ? "38%" : "auto",
        }}
      >
        {([OptionSideKey.A, OptionSideKey.B] as const).map((key) => {
          const text = key === OptionSideKey.A ? reveal.option_a : reveal.option_b;
          const style = halfStyle(key);
          return (
            <div
              key={key}
              className="absolute inset-x-0 transition-all duration-500"
              style={{
                ...style,
                backgroundColor: SIDES[key].color,
                color: SIDES[key].ink,
              }}
            >
              {/* Identical fit bounds to the turn screen. The reveal opens as
                  its continuation, so nothing resizes under the countdown.

                  Both options stay readable through the countdown even on an
                  invalid turn — the room was still guessing. The text goes only
                  once the surviving card has risen with nothing to say. */}
              <div className="h-full w-full px-5 py-6">
                {reveal.valid || !risen ? (
                  <FitText max={40} min={13}>
                    {text}
                  </FitText>
                ) : null}
              </div>
            </div>
          );
        })}

        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-center justify-center gap-2 py-3"
          style={{ backgroundColor: `${palette.bg}D9` }}
        >
          <AvatarFace seed={target?.avatar_seed ?? ""} size={22} />
          <span className="text-sm font-bold uppercase tracking-tight text-ink">
            {reveal.valid ? choseParts[0] : null}
            <span
              style={{
                color: nameColorFor(target?.avatar_seed ?? "", NAME_COLORS),
              }}
            >
              {target?.display_name ?? ""}
            </span>
            {reveal.valid
              ? choseParts[1]
              : t.reveal.didNotChooseSuffix}
          </span>
        </div>

        {/* 3 · 2 · 1, on the seam where the OrPill sits during the turn. */}
        {counting && count > 0 && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
            <div
              key={count}
              className="flex h-20 w-20 items-center justify-center rounded-full text-4xl font-bold tabular-nums"
              style={{
                backgroundColor: palette.bg,
                color: palette.ink,
                animation: "badorbad-count 400ms ease-out",
              }}
            >
              {count}
            </div>
          </div>
        )}

        {/* The signature, over the option it belongs to. */}
        {author && (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-center justify-center gap-2 py-3 transition-all duration-500"
            style={{
              opacity: showAuthor ? 1 : 0,
              transform: showAuthor ? "translateY(0)" : "translateY(8px)",
              backgroundColor: `${palette.bg}D9`,
            }}
          >
            <AvatarFace seed={author.avatar_seed} size={22} />
            <span className="text-sm font-bold uppercase tracking-tight text-ink">
              {t.reveal.writtenBy.split("%s")[0]}
              <span style={{ color: nameColorFor(author.avatar_seed, NAME_COLORS) }}>
                {author.display_name}
              </span>
            </span>
          </div>
        )}
      </div>

      {/*
        The summary. Your result first, then the room.

        It takes no space at all until it is due — `flex-1` here would have the
        options sharing the screen with an invisible block through the whole
        countdown, so they would open at half the height they had on the turn
        screen. Collapsing to zero basis keeps the split full-size until the
        one moment it is supposed to yield.
      */}
      <div
        className="flex min-h-0 flex-col justify-center gap-3 overflow-hidden px-3 transition-all duration-500"
        style={{
          flexGrow: showSummary ? 1 : 0,
          flexBasis: 0,
          paddingTop: showSummary ? 12 : 0,
          opacity: showSummary ? 1 : 0,
        }}
      >
        <p
          className="text-center text-lg font-bold uppercase tracking-tight"
          style={{
            color: amTarget
              ? palette.ink
              : mine?.correct
                ? palette.correct
                : palette.wrong,
          }}
        >
          {!reveal.valid
            ? t.reveal.skipped
            : amTarget
              ? fmt(
                  t.reveal.youWereRead,
                  String(reveal.correct_count),
                  String(reveal.guesses.length),
                )
              : mine?.correct
                ? t.reveal.youGuessedRight
                : t.reveal.youGuessedWrong}
        </p>

        <div className="flex items-start gap-2">
          <Cluster
            label={t.reveal.clusterRight}
            tone={palette.correct}
            guesses={right}
            players={players}
          />
          <div className="w-px self-stretch bg-surface" />
          <Cluster
            label={t.reveal.clusterWrong}
            tone={palette.wrong}
            guesses={wrong}
            players={players}
          />
        </div>
      </div>

      <Reactions />

      <div className="flex flex-col gap-2 px-3 pb-3 pt-2">
        <div className="flex justify-center gap-2">
          {emojiSet.map((emoji) => (
            <button
              key={emoji}
              data-sound="reaction"
              aria-label={fmt(t.reveal.react, emoji)}
              className="flex h-12 w-12 select-none touch-manipulation items-center justify-center rounded-full text-2xl transition-transform active:scale-90"
              onClick={() => {
                react(emoji);
              }}
            >
              {emoji}
            </button>
          ))}
        </div>

        {isHost && (
          <button
            disabled={!showSummary}
            className="w-full py-3 text-sm font-bold uppercase tracking-widest disabled:cursor-not-allowed disabled:opacity-40"
            style={{ backgroundColor: palette.ink, color: palette.bg }}
            onClick={advanceReveal}
          >
            {t.reveal.continue}
          </button>
        )}
      </div>
    </div>
  );
};
